/**
 * "Esqueci minha senha": pede o e-mail e dispara o link.
 *
 * A resposta na tela é a mesma exista o e-mail ou não — é a API que garante
 * isso, e a tela não tenta ser mais esperta que ela. A única exceção é o
 * servidor sem SMTP: aí não há como entregar, e fingir que enviou deixaria a
 * pessoa olhando a caixa de entrada à toa. Nesse caso o caminho é o
 * administrador redefinir pela tela de Usuários.
 */
import { KeyRound, MailCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { MolduraAcesso } from '@/components/MolduraAcesso';
import { ApiError, apiFetch } from '@/lib/api';


type Estado = 'inicial' | 'enviando' | 'enviado' | 'indisponivel';

export function EsqueciSenhaPage() {
  const [email, setEmail] = useState('');
  const [estado, setEstado] = useState<Estado>('inicial');
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(ev: FormEvent) {
    ev.preventDefault();
    setErro(null);
    setEstado('enviando');
    try {
      const r = await apiFetch<{ delivery: 'sent' | 'unavailable' }>('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
        skipAuth: true,
      });
      setEstado(r.delivery === 'unavailable' ? 'indisponivel' : 'enviado');
    } catch (e) {
      setEstado('inicial');
      setErro(
        e instanceof ApiError && e.status === 429
          ? 'Muitas tentativas. Aguarde alguns minutos e tente de novo.'
          : 'Não foi possível enviar agora. Tente de novo em instantes.',
      );
    }
  }

  return (
    <MolduraAcesso titulo="Esqueci minha senha" icone={<KeyRound className="h-5 w-5" />}>
      {estado === 'enviado' && (
        <div className="space-y-3 text-sm">
          <p className="flex items-start gap-2">
            <MailCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
            <span>
              Se <b>{email.trim()}</b> tiver cadastro, o link para criar uma nova senha chega em
              instantes. Ele vale por <b>30 minutos</b>.
            </span>
          </p>
          <p className="text-xs text-muted-foreground">
            Não chegou? Confira a caixa de spam. Pedir de novo cancela o link anterior.
          </p>
          <Link to="/login" className="block text-center text-xs underline-offset-2 hover:underline">
            Voltar ao login
          </Link>
        </div>
      )}

      {estado === 'indisponivel' && (
        <div className="space-y-3 text-sm">
          <p>
            Este servidor ainda não está configurado para enviar e-mail, então o link não pode
            ser entregue.
          </p>
          <p>
            Peça ao administrador do sistema para <b>redefinir sua senha</b> — ele faz isso na tela
            de Usuários e te passa a nova. Depois é só trocar por uma sua em &quot;Alterar
            senha&quot;, no menu.
          </p>
          <Link to="/login" className="block text-center text-xs underline-offset-2 hover:underline">
            Voltar ao login
          </Link>
        </div>
      )}

      {(estado === 'inicial' || estado === 'enviando') && (
        <form onSubmit={enviar} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Informe o e-mail do seu acesso. Enviamos um link para você criar uma senha nova.
          </p>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">E-mail</span>
            <input
              type="email"
              required
              autoComplete="email"
              autoFocus
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={estado === 'enviando'}
              placeholder="seu.email@empresa.com"
            />
          </label>
          {erro && (
            <p role="alert" className="text-sm text-destructive">
              {erro}
            </p>
          )}
          <button
            type="submit"
            disabled={estado === 'enviando'}
            className="w-full rounded-md bg-primary py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {estado === 'enviando' ? 'Enviando…' : 'Enviar link'}
          </button>
          <Link to="/login" className="block text-center text-xs underline-offset-2 hover:underline">
            Voltar ao login
          </Link>
        </form>
      )}
    </MolduraAcesso>
  );
}
